import React from 'react';
import styled, { css } from 'styled-components';
import { useProjectDetailActions, useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { CoverHeight, DeviceType, DividerType, ProjectType, ServiceType } from '../../shared/enum/EnumRepository';
import { InputTitle } from '../../shared/component/headless/input/InputBody';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import { useProjectStore } from '../../shared/store/projectStore';
import { StyledImgDivContainerProps } from '../../shared/dto/StyleCompRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { UpdateProjectReq } from '../../shared/dto/ReqDtoRepository';
import { useImage } from '../../shared/hooks/useApi/useImage';
import OptimizedMoleculeImgDivContainer from '../../shared/component/molecule/OptimizedMoleculeImgDivContainer';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import Divider from '../../shared/Divider';
import SelectBox from '../../shared/component/SelectBox';
import { SelectType } from '../../shared/enum/EnumRepository';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { getPageMessage } from '../../shared/constants/pageMessages';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useValidation } from '../../shared/hooks/useValidation';
import { generateYearOptions } from '../../shared/util/yearOptions';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import { useCoverImagePan } from '../../shared/hooks/useCoverImagePan';

const yearOptions = generateYearOptions();

const ProjectDetailContainer: React.FC = () => {
  const { project } = useProjectDetailState();
  const {
    updateProjectHeader,
    updateProjectCoverHeight,
    updateProjectType,
    updateProjectYear,
  } = useProjectDetailActions();
  const handleTitleChange = useProjectStore((s) => s.updateTitle);
  const handleImageChange = useProjectStore((s) => s.updateImage);
  const handleFocusChange = useProjectStore((s) => s.updateFocus);
  const hasChanged = useProjectStore((s) => s.hasChanged);
  const { uploadImage } = useImage();
  const { isEditMode } = useEditMode();
  const { deviceType } = useDeviceStore();
  const { checkType } = useValidation();
  const [isCoverResizing, setIsCoverResizing] = React.useState(false);
  const previousCoverHeightRef = React.useRef(project?.coverHeight);
  const focusX = project?.focusX ?? 0.5;
  const focusY = project?.focusY ?? 0.5;

  React.useEffect(() => {
    const coverHeight = project?.coverHeight;
    if (!coverHeight) return;
    if (!previousCoverHeightRef.current) {
      previousCoverHeightRef.current = coverHeight;
      return;
    }
    if (previousCoverHeightRef.current === coverHeight) return;

    previousCoverHeightRef.current = coverHeight;
    setIsCoverResizing(true);
    const timeoutId = window.setTimeout(() => setIsCoverResizing(false), 650);
    return () => window.clearTimeout(timeoutId);
  }, [project?.coverHeight]);

  const updateFocus = React.useCallback(
    (nextFocusX: number, nextFocusY: number) => {
      handleFocusChange(nextFocusX, nextFocusY);
    },
    [handleFocusChange],
  );

  const { canPan, isDragging, panHandlers } = useCoverImagePan({
    imageWidth: project?.uploadFile.width ?? 0,
    imageHeight: project?.uploadFile.height ?? 0,
    focusX,
    focusY,
    isEnabled:
      isEditMode &&
      !!project?.uploadFile.originUrl &&
      !isCoverResizing,
    onFocusChange: updateFocus,
  });
  
  if (!project) {
    return null;
  }

  const handleProjectHeaderUpdate = async () => {
    if (!project) return;

    let request: UpdateProjectReq = { id: project.id };

    if (project.titleChanged) {
      request.title = project.title;
    }

    if (project.imageChanged) {
      request = {
        ...request,
        updateUploadFileReq: {
          originUrl: project.uploadFile.originUrl,
          uploadFileId: project.uploadFile.id,
          allowDownload: project.uploadFile.allowDownload,
          width: project.uploadFile.width,
          height: project.uploadFile.height,
        },
        focusX,
        focusY,
      };
      const finalRequest = await uploadImage(ServiceType.PROJECT, request);
      if (!finalRequest) return;
      request = finalRequest as UpdateProjectReq;
    } else if (project.focusChanged) {
      request.focusX = focusX;
      request.focusY = focusY;
    }

    await updateProjectHeader(request);
  };

  const handleHeightChange = async (value: CoverHeight) => {
    await updateProjectCoverHeight({
      id: project.id,
      coverHeight: value,
    });
  };

  const handleProjectTypeChange = async (value: ProjectType) => {
    await updateProjectType({
      id: project.id,
      projectType: value,
    });
  };

  const handleProjectYearChange = async (value: string) => {
    if (!project) return;
    const projectYear = value === '' ? null : Number(value);
    if (value !== '' && !checkType('year', value)) {
      return;
    }
    if (project.projectYear === projectYear) {
      return;
    }
    await updateProjectYear({
      id: project.id,
      projectYear,
    });
  };

  return (
    <ProjectDetailContainerComp
      data-has-changed={hasChanged ? "true" : "false"}  // 편집 모드 진입 시 변경사항이 있으면 스크롤
    >
      <OptimizedMoleculeImgDivContainer
        backgroundImg={convertS3UrlToCloudFrontUrl(project.uploadFile.originUrl)}
        handleChange={handleImageChange}
        StyledImgDivContainer={RepresentImgContainer}
        $coverHeight={project.coverHeight}
        $focusX={focusX}
        $focusY={focusY}
        $canPan={canPan}
        $isDragging={isDragging}
        {...panHandlers}
      />
      <ProjectDetailWrapper $isMobile={deviceType === DeviceType.MOBILE} $isEditMode={isEditMode}>
        {isEditMode &&
        <ProjectDetailOptionContainer $isMobile={deviceType === DeviceType.MOBILE}>
          <SelectBoxWrapperProjectType>
            <SelectBox
              value={project.projectType}
              selectType={SelectType.PROJECT_TYPE}
              handleChange={handleProjectTypeChange}
              direction={false} />
          </SelectBoxWrapperProjectType>
          <YearSelect
            value={project.projectYear ?? ''}
            onChange={(e) => handleProjectYearChange(e.target.value)}
          >
            <option value="">YEAR</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </YearSelect>
          <SelectBoxWrapperCoverHeight>
            <SelectBox
              value={project.coverHeight}
              selectType={SelectType.COVER_HEIGHT}
              handleChange={handleHeightChange}
              direction={false} />
          </SelectBoxWrapperCoverHeight>
        </ProjectDetailOptionContainer>
        }
        <TitleContainer>
          <MoleculeInputDiv
            value={project.title}
            placeholder={getPageMessage('PROJECT_TITLE_PLACEHOLDER')}
            handleChange={(e) => handleTitleChange(e.target.value)}
            inputStyle={InputTitle}
            StyledDiv={Title}
          />
          {isEditMode && (project.titleChanged || project.imageChanged || project.focusChanged) &&
            <UniversalIconBtn 
              isPrimary={true}
              size="fit" 
              border={false} 
              backgroundImg={confirmIcon} 
              backgroundImgHover={confirmIcon2}
              onClick={handleProjectHeaderUpdate} />
          }
        </TitleContainer>
        <Divider dividerType={DividerType.PLAIN} bottom={deviceType === DeviceType.MOBILE ? "30px" : "40px"} />
      </ProjectDetailWrapper>
    </ProjectDetailContainerComp >
  );
}

const ProjectDetailContainerComp = styled.section`
  position: relative;
`;

const ProjectDetailOptionContainer = styled.div<{ $isMobile: boolean }>`
  position: absolute;
  top: ${({ $isMobile }) => $isMobile ? '0px' : 'calc(0.5vh)'};
  right: ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px)' : 'calc(8vw)'};
  width: fit-content;
  display: flex;
  gap: clamp(12px, 1vw, 14px);
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(12px, 4vw, 56px);
  flex: 1;
  min-width: 0;
`;

const SelectBoxWrapperProjectType = styled.div`
  display: flex;
  align-items: center;
  width: clamp(100px, 8vw, 120px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const SelectBoxWrapperCoverHeight = styled.div`
  display: flex;
  align-items: center;
  width: clamp(60px, 5.5vw, 80px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const YearSelect = styled.select`
  width: clamp(60px, 5.5vw, 80px);
  height: fit-content;
  padding: 1px 0px;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  border-radius: 0;
  background: transparent;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  cursor: pointer;

  option {
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

const Title = styled.div`
  padding-bottom: 4px;
  ${({ theme }) => theme.typography.Body_00_1};
`;

const RepresentImgContainer = styled.div<StyledImgDivContainerProps & {
  $coverHeight: CoverHeight;
  $focusX: number;
  $focusY: number;
  $canPan: boolean;
  $isDragging: boolean;
}>`
  position: relative;
  background-image: url(${props => props.$backgroundImg});
  background-size: cover;
  background-position: ${({ $focusX, $focusY }) => `${$focusX * 100}% ${$focusY * 100}%`};
  background-repeat: no-repeat;
  touch-action: ${({ $canPan }) => ($canPan ? 'none' : 'auto')};
  cursor: ${({ $canPan, $isDragging }) => {
    if (!$canPan) return 'default';
    return $isDragging ? 'grabbing' : 'grab';
  }};
  user-select: none;
  height: ${props => {
    switch (props.$coverHeight) {
      case CoverHeight.FULL:
        return 'calc(240vw / 6)';
      case CoverHeight.HALF:
        return 'calc(240vw / 8)';
      default:
        return 'calc(240vw / 4)';
    }
  }};
  max-height: 98vh;
  transition: height 0.6s ease-in-out;
`;

const ProjectDetailWrapper = styled.article<{ $isMobile: boolean, $isEditMode: boolean }>`
  position: relative;
  padding: ${({ $isMobile }) => $isMobile ? '2vh clamp(18px, 5vw, 24px) 0 clamp(18px, 5vw, 24px)' : '3vh 8vw 0 8vw'};
  ${({ $isEditMode, $isMobile }) => $isEditMode && $isMobile && css`padding-top: calc(3vh);`}
`;

export default ProjectDetailContainer;
